import { ChangeEvent, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { addPortalUser } from 'lib/firebase';
import styles from './InviteUserModal.module.scss';
import { Close } from '@mui/icons-material';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { useAuth } from 'hooks';
import { useSelector } from 'react-redux';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';

interface InviteUserModalProps extends DefaultOpenModalProps {
    onUserInvited: () => void;
}

export const InviteUserModal = ({
    modalOpen,
    setModalOpen,
    onUserInvited,
}: InviteUserModalProps) => {
    const { user } = useAuth();

    const [workingOnInvitingUser, setWorkingOnInvitingUser] = useState(false);

    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);

    const [addUserFieldText, setAddUserFieldText] = useState('');

    const inviteUser = () => {
        setWorkingOnInvitingUser(true);
        const supplierId =
            viewAsSupplier !== ''
                ? viewAsSupplier
                : user?.protected?.supplierId;

        if (supplierId) {
            addPortalUser(supplierId, addUserFieldText).then(() => {
                onUserInvited();
                setModalOpen(false);
            });
        }
    };

    const addUserFieldChanged = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setAddUserFieldText(event.target.value);
    };

    useEffect(() => {
        setWorkingOnInvitingUser(false);
    }, [modalOpen]);

    return (
        <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            className={styles.InviteUserModal}
            data-testid="InviteUserModal"
        >
            <Toolbar>
                <Typography className={styles.modalHeader} variant="h6">
                    Invite User
                </Typography>
                <IconButton
                    color="inherit"
                    onClick={() => setModalOpen(false)}
                    aria-label="close"
                >
                    <Close />
                </IconButton>
            </Toolbar>
            <DialogContent>
                <DialogContentText>
                    Enter the email address of the new user.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="email"
                    label="Email"
                    type="email"
                    fullWidth
                    onChange={addUserFieldChanged}
                />
            </DialogContent>
            <DialogActions>
                {workingOnInvitingUser && (
                    <CircularProgress className={styles.spinner} />
                )}
                <Button
                    variant="contained"
                    color="warning"
                    disabled={workingOnInvitingUser}
                    onClick={() => setModalOpen(false)}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={workingOnInvitingUser}
                    onClick={inviteUser}
                >
                    Invite
                </Button>
            </DialogActions>
        </Dialog>
    );
};
